import { ResponseParams, ApiError, requestHttpGet, requestHttpPost } from './RequestBase';
import { AuthorizeV1Data } from 'store/connect/AuthorizeV1Data';
import { AuthorizeV2Data } from 'store/connect/AuthorizeV2Data';
import { RedirectLoginData } from 'store/connect/RedirectLoginData';
import { User } from 'requests/RequestUser';

export type TermsRedirectResData = {
	type: string; // terms, attribute
	isFirstAgreement: boolean;
};
type LoginReqData = {
	gid: string;
	pw: string;
};
export type LoginResData = {
	gid_short: GidShort;
	gid_data: User;
};
type GidShort = {
	gidpk: string;
	gid: string;
	accesstoken: string;
	refreshtoken: string;
};
// ログイン
export const callLoginApi = async (
	body: LoginReqData,
): Promise<ResponseParams<LoginResData | TermsRedirectResData | ApiError>> => {
	const result: ResponseParams<LoginResData | ApiError> = await requestHttpPost(`/login`, body);
	return result;
};

export type CommonLoginV1ReqData = AuthorizeV1Data & LoginReqData;
export type CommonLoginV1ResData = {
	ctdata: string; // 連携トークン
	scd: string; // ポータルのサービスサイトコード
};
// 共通ログインv1
export const callCommonLoginV1Api = async (
	body: CommonLoginV1ReqData,
): Promise<ResponseParams<CommonLoginV1ResData | TermsRedirectResData | ApiError>> => {
	const result: ResponseParams<CommonLoginV1ResData | ApiError> = await requestHttpPost(`/v1/common-login`, body);
	return result;
};

// 規約同意後共通ログインv1続き
export const callCommonLoginV1AfterAgreeApi = async (
	body: AuthorizeV1Data,
): Promise<ResponseParams<CommonLoginV1ResData>> => {
	const result: ResponseParams<CommonLoginV1ResData> = await requestHttpPost(`/v1/common-login-after-agree`, body);
	return result;
};

export type CommonLoginV2ReqData = AuthorizeV2Data & LoginReqData;
export type CommonLoginV2ResData = {
	code: string; // 認可コード
	state?: string;
	gid_registered?: string; // 下位互換用パラメータ。ID取得から遷移した場合は1をセット
};
// 共通ログインv2
export const callCommonLoginV2Api = async (
	body: CommonLoginV2ReqData,
): Promise<ResponseParams<CommonLoginV2ResData | TermsRedirectResData | ApiError>> => {
	const result: ResponseParams<CommonLoginV2ResData | ApiError> = await requestHttpPost(`/v2/common-login`, body);
	return result;
};

// 規約同意後共通ログインv2続き
export const callCommonLoginV2AfterAgreeApi = async (
	body: AuthorizeV2Data,
): Promise<ResponseParams<CommonLoginV2ResData | TermsRedirectResData>> => {
	const result: ResponseParams<CommonLoginV2ResData | TermsRedirectResData> = await requestHttpPost(
		`/v2/common-login-after-agree`,
		body,
	);
	return result;
};

export type RequiredProfileEditRedirectResData = {
	type: string;
};
// 不足属性入力後共通ログインv2続き
export const callCommonLoginV2AfterRequiredProfileEditApi = async (
	body: AuthorizeV2Data,
): Promise<ResponseParams<CommonLoginV2ResData | RequiredProfileEditRedirectResData>> => {
	const result: ResponseParams<CommonLoginV2ResData | RequiredProfileEditRedirectResData> = await requestHttpPost(
		`/v2/common-login-after-required-profile-edit`,
		body,
	);
	return result;
};

// 属性提供同意後共通ログインv2続き
export const callCommonLoginV2AfterAttributeAgreeApi = async (
	body: AuthorizeV2Data,
): Promise<ResponseParams<CommonLoginV2ResData>> => {
	const result: ResponseParams<CommonLoginV2ResData> = await requestHttpPost(
		`/v2/common-login-after-attribute-agree`,
		body,
	);
	return result;
};

export type RedirectLoginReqData = RedirectLoginData & LoginReqData;

// リダイレクトログイン
export const callRedirectLoginApi = async (
	body: RedirectLoginReqData,
): Promise<ResponseParams<LoginResData | TermsRedirectResData | ApiError>> => {
	const result: ResponseParams<LoginResData | ApiError> = await requestHttpPost(`/redirect-login`, body);
	return result;
};

// 規約同意後リダイレクトログイン続き
export const callRedirectLoginAfterAgreeApi = async (
	body: AuthorizeV2Data,
): Promise<ResponseParams<CommonLoginV2ResData | TermsRedirectResData>> => {
	const result: ResponseParams<CommonLoginV2ResData | TermsRedirectResData> = await requestHttpPost(
		`/v2/common-login-after-agree`,
		body,
	);
	return result;
};

// 属性提供同意後リダイレクトログインv2続き
export const callRedirectLoginV2AfterAttributeAgreeApi = async (
	body: AuthorizeV2Data,
): Promise<ResponseParams<CommonLoginV2ResData>> => {
	const result: ResponseParams<CommonLoginV2ResData> = await requestHttpPost(
		`/v2/common-login-after-attribute-agree`,
		body,
	);
	return result;
};

type CheckLoggedInResData = {
	isLoggedIn: boolean;
	loginUser: User;
};
// ログイン済みチェック
export const callCheckLoggedInApi = async (): Promise<ResponseParams<CheckLoggedInResData>> => {
	const result: ResponseParams<CheckLoggedInResData> = await requestHttpGet(`/check-loggedin`);
	return result;
};

export type GetLoginDisplayItemReqData = {
	gid_dp?: string;
	site_cd: string;
};
export type GetLoginDisplayItemResData = {
	manage_id: string;
	site_cd: string;
	logo: string;
	title: string;
	title_size: number;
	title_weight: number;
	description: string;
	description_size: number;
	description_weight: number;
	redirect_url: string;
	member_regist_complete: string;
	terms_agree_complete: string;
	initial_registration_display_flg: number;
	initial_registration_url: string;
	initial_registration_link_display: string;
	forget_password_display_flg: number;
	footer_display_flg: number;
};
// ログイン画面設定取得
export const callGetLoginDisplayItemApi = async (
	query: GetLoginDisplayItemReqData,
): Promise<ResponseParams<GetLoginDisplayItemResData>> => {
	const result: ResponseParams<GetLoginDisplayItemResData> = await requestHttpGet(`/login/display-item`, query);
	return result;
};

// ログアウト
export const callLogoutApi = async () => {
	const result = await requestHttpPost(`/logout`, {});
	return result;
};

// シングルログアウト
export const callSingleLogoutApi = async () => {
	const result = await requestHttpPost(`/single-logout`, {});
	return result;
};
